import { css } from '@emotion/react'
import {
  CornerRadius,
  gemVar,
  LineWeight,
  Shadow,
  ShadowElevation,
  Spacing,
  ThemedColors,
  Typography,
} from '@opallabs/gem'
import { CalendarSizes } from '../sizes'

/**
 * How wide the ghost is, whatever it stands for. The design draws every ghost
 * at this width, so a moment reaching across a week and a card two lines tall
 * both condense to the same one-line shape under the pointer.
 *
 * Figma: "Month - states", the Drag A row (4030:24338).
 */
const ghostInlineSize = 176

/**
 * As on the grid: the design rounds the count badge to 3px, between Gem's
 * `XSmall` and `Small`.
 */
const countCornerRadius = 3

/**
 * The one shape a dragged item takes. It borrows the grid's own chip and bar —
 * their height, their type, their leading swatch — and adds the two things that
 * make it a ghost: a width it never exceeds, and a shadow that holds it off the
 * page.
 *
 * It cannot reuse the grid's classes: those rules are nested under the grid's
 * root, and the browser renders a drag image from a container appended to the
 * body.
 */
export const ghost = css({
  ...Typography.T300,
  alignItems: 'center',
  blockSize: CalendarSizes.chipBlockSize,
  borderRadius: CornerRadius.Small,
  boxShadow: Shadow[ShadowElevation.E3].boxShadow,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextPrimary),
  display: 'flex',
  inlineSize: ghostInlineSize,
  overflow: 'hidden',
  paddingInline: Spacing.XS,
  textAlign: 'start',
  whiteSpace: 'nowrap',
})

/**
 * Content keeps the chip's white face and its border, a shade darker than the
 * chip's own so the ghost reads as lifted rather than as a chip left behind.
 */
export const contentGhost = css({
  backgroundColor: gemVar(ThemedColors.Surface1),
  borderColor: gemVar(ThemedColors.StrokeSecondary3),
  borderStyle: 'solid',
  borderWidth: LineWeight.Small,
})

/**
 * The color the item carries, down the leading edge as the grid draws it. The
 * design holds the swatch in a 4px slot: 3px of color, then 1px of space
 * before the first mark.
 */
export const swatch = css({
  blockSize: 12,
  borderRadius: CornerRadius.XSmall,
  flex: 'none',
  inlineSize: 3,
  marginInlineEnd: 1,
})

/** The channel mark on content, the kind mark on a moment. */
export const mark = css({
  alignItems: 'center',
  blockSize: CalendarSizes.barIconSize,
  display: 'flex',
  flex: 'none',
  inlineSize: CalendarSizes.barIconSize,
  justifyContent: 'center',
})

/**
 * The name holds only the room it needs, so a moment's count sits beside it
 * rather than at the far edge. Past the width the ghost has, the name is the
 * only thing that gives way.
 */
export const name = css({
  flex: '0 1 auto',
  marginInlineStart: 2,
  minInlineSize: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

/** The time content goes live at, ahead of its name. */
export const time = css({ marginInlineEnd: 2 })

/** How many pieces of content the moment holds. */
export const count = css({
  ...Typography.HL25,
  alignItems: 'center',
  // An opaque surface keeps the badge legible on any tint.
  backgroundColor: gemVar(ThemedColors.Surface1),
  blockSize: 12,
  borderRadius: countCornerRadius,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextSecondary),
  display: 'inline-flex',
  flex: 'none',
  justifyContent: 'center',
  marginInlineStart: Spacing.XS,
  minInlineSize: 12,
  paddingInline: 2,
})
